/** When a purchase order's goods are expected and whether they are late, so the Orders list can prompt a chase. */
import type { ProjectPurchaseOrder } from '../../../types/projectProcurement';
import { isOpenPurchaseOrder } from '../../../shared-ui/purchases/purchaseOrderStatus';

export type PurchaseOrderDeliveryStatus =
  | { kind: 'overdue'; days: number; label: string }
  | { kind: 'due-soon'; days: number; label: string };

const DUE_SOON_DAYS = 2;
const DAY_MS = 86_400_000;
// Lead time comes from a free-text input; beyond this the suggestion is noise and a huge value would make Date throw.
const MAX_LEAD_TIME_DAYS = 3650;

// Compared as calendar days at UTC midnight so the time of day never shifts the count.
const dayNumber = (isoDate: string): number => Math.floor(Date.parse(`${isoDate.slice(0, 10)}T00:00:00Z`) / DAY_MS);

const plural = (days: number): string => `${days} ${days === 1 ? 'day' : 'days'}`;

/** `today` is a local calendar date (YYYY-MM-DD). */
export function purchaseOrderDeliveryStatus(
  po: Pick<ProjectPurchaseOrder, 'status' | 'expectedDeliveryDate'>,
  today: string,
): PurchaseOrderDeliveryStatus | null {
  // Only orders still waiting on goods can be late; a received, cancelled or draft order never is.
  if (!isOpenPurchaseOrder(po.status) || !po.expectedDeliveryDate) return null;
  const due = dayNumber(po.expectedDeliveryDate);
  const now = dayNumber(today);
  if (Number.isNaN(due) || Number.isNaN(now)) return null;
  const daysLate = now - due;
  if (daysLate > 0) return { kind: 'overdue', days: daysLate, label: `Delivery overdue by ${plural(daysLate)}` };
  if (daysLate === 0) return { kind: 'due-soon', days: 0, label: 'Delivery due today' };
  if (-daysLate <= DUE_SOON_DAYS) return { kind: 'due-soon', days: -daysLate, label: `Delivery due in ${plural(-daysLate)}` };
  return null;
}

/**
 * The date a merchant's quoted lead time implies, as YYYY-MM-DD, or null when the quote gave none.
 * Lead time is counted in calendar days from `today` (a local calendar date); the buyer can overwrite it.
 */
export function expectedDeliveryFromLeadTime(leadTimeDays: number | null | undefined, today: string): string | null {
  if (leadTimeDays == null || !Number.isInteger(leadTimeDays) || leadTimeDays <= 0 || leadTimeDays > MAX_LEAD_TIME_DAYS) return null;
  const start = dayNumber(today);
  if (Number.isNaN(start)) return null;
  return new Date((start + leadTimeDays) * DAY_MS).toISOString().slice(0, 10);
}

/**
 * The quoted lead time (in days) a purchase order's expected date still reflects, or null once the buyer has changed it.
 * Derived rather than stored: the server rebuilds an issued order from fixed fields, so a flag would not survive.
 */
export function leadTimeBehindExpectedDate(
  po: Pick<ProjectPurchaseOrder, 'expectedDeliveryDate' | 'issuedAt'>,
  leadTimeDays: number | null | undefined,
): number | null {
  if (!po.expectedDeliveryDate || leadTimeDays == null) return null;
  const issued = new Date(po.issuedAt);
  if (Number.isNaN(issued.getTime())) return null;
  const implied = expectedDeliveryFromLeadTime(leadTimeDays, issued.toLocaleDateString('en-CA'));
  return implied !== null && implied === po.expectedDeliveryDate.slice(0, 10) ? leadTimeDays : null;
}
